import { Box, Stack, Text, useMantineTheme } from '@mantine/core';
import { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { TccData } from '../types';
import { latest, pctChange, previous } from '../helpers';
import { scoreAllTimeCountGoal } from '../../statusUtils';
import { CardShell } from './CardShell';
import { MetricRow } from './MetricRow';

export function CredentialsAwardedCard({ data }: { data: TccData }) {
  const theme = useMantineTheme();
  const series = data.credentialsAwarded;
  const cur = series.length > 0 ? latest(series) : null;
  const prev = previous(series);

  const allTime = data.credentialsAwardedAllTimeTotal;
  const scored = scoreAllTimeCountGoal({
    value: allTime,
    goals: data.goalsByMetric['tcc.credentialsAwarded'],
    field: 'credentialsAwarded',
    quarterLabel: cur?.quarter ?? '',
  });
  const goalAllTime = scored.goal;
  const status = scored;

  const qoq = cur ? pctChange(cur.credentialsAwarded, prev?.credentialsAwarded ?? null) : null;
  const arrow = (n: number): '↑' | '↓' | '' => (n > 0 ? '↑' : n < 0 ? '↓' : '');
  const qoqText = qoq === null ? '—' : arrow(qoq) ? `${arrow(qoq)}${Math.abs(qoq).toFixed(2)}% QoQ` : '0.00% QoQ';

  const parseQuarter = (label: string): { year: number; quarter: number } | null => {
    const match = label.match(/^Q([1-4])\s+(\d{4})$/);
    if (!match) return null;
    return { year: Number(match[2]), quarter: Number(match[1]) };
  };

  const currentMeta = parseQuarter(cur?.quarter ?? '');
  const ytd = currentMeta
    ? series
        .filter((point) => {
          const meta = parseQuarter(point.quarter);
          return meta?.year === currentMeta.year && meta.quarter <= currentMeta.quarter;
        })
        .reduce((sum, point) => sum + point.credentialsAwarded, 0)
    : 0;

  const yoy = (() => {
    if (!currentMeta) return null;
    const prevYearTotal = series
      .filter((point) => {
        const meta = parseQuarter(point.quarter);
        return meta?.year === currentMeta.year - 1 && meta.quarter <= currentMeta.quarter;
      })
      .reduce((sum, point) => sum + point.credentialsAwarded, 0);
    return pctChange(ytd, prevYearTotal);
  })();
  const yoyText = yoy === null ? '— YoY' : yoy === 0 ? '0.00% YoY' : `${arrow(yoy)}${Math.abs(yoy).toFixed(2)}% YoY`;

  const tooltipShellStyle = {
    backgroundColor: theme.colorScheme === 'dark' ? '#000' : '#fff',
    border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}`,
    borderRadius: 10,
    color: theme.colorScheme === 'dark' ? '#fff' : '#000',
    padding: '10px 12px',
    maxWidth: 320,
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
  } as const;

  const seriesTotal = series.reduce((sum, point) => sum + point.credentialsAwarded, 0);
  const priorCredentials = Math.max(0, allTime - seriesTotal);
  let cumulative = priorCredentials;
  const chartData = series.map((point) => {
    cumulative += point.credentialsAwarded;
    return {
      ...point,
      cumulative,
    };
  });
  const maxCumulative = Math.max(allTime, ...chartData.map((p) => p.cumulative));

  return (
    <CardShell
      title="Credentials Awarded"
      subtitle={cur?.quarter ?? 'No data'}
      statusLabel={status.label}
      statusColor={status.color}
      statusTooltip={status.tooltip}
    >
      <Stack spacing="sm">
        <MetricRow
          items={[
            {
              label: 'This Quarter',
              labelTooltip: 'Unique awardings of stackable micro-credentials this quarter.',
              value: (
                <Text size="lg" weight={800}>
                  {cur?.credentialsAwarded ?? '—'}
                </Text>
              ),
              sublabel: qoqText,
            },
            {
              label: 'YTD',
              labelTooltip: 'Calendar year to date credentials awarded (sum of quarters in the current year through this quarter).',
              value: (
                <Text size="lg" weight={800}>
                  {ytd}
                </Text>
              ),
              sublabel: yoyText,
            },
            {
              label: 'All-Time',
              labelTooltip:
                goalAllTime != null
                  ? `Total credentials awarded across all time (includes periods not shown). Goal: ${goalAllTime} credentials.`
                  : 'Total credentials awarded across all time (includes periods not shown).',
              value: (
                <Text size="lg" weight={800}>
                  {allTime}
                </Text>
              ),
              sublabel: goalAllTime != null ? `Goal ${goalAllTime}` : undefined,
            },
          ]}
        />

        <Text size="xs" color="dimmed" weight={700} sx={{ letterSpacing: 0.2, marginTop: '0.75rem' }}>
          Cumulative Credentials Awarded
        </Text>

        <Box sx={{ height: 170 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.18} vertical={false} />
              <XAxis dataKey="quarter" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={12} />
              <YAxis
                orientation="right"
                tick={{ fontSize: 11 }}
                allowDecimals={false}
                axisLine={false}
                tickLine={false}
                width={44}
                tickMargin={8}
                domain={[0, maxCumulative]}
              />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload || payload.length === 0) return null;
                  const p: any = payload[0]?.payload;
                  const v = p?.cumulative;
                  const quarterValue = p?.credentialsAwarded;
                  return (
                    <div style={tooltipShellStyle as any}>
                      <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 12, opacity: 0.9 }}>{label}</div>
                      <div style={{ fontSize: 12, lineHeight: 1.35 }}>
                        <div style={{ fontWeight: 900, fontSize: 14 }}>{typeof v === 'number' ? v : '—'} Total</div>
                        <div style={{ opacity: 0.9 }}>
                          {typeof quarterValue === 'number' ? quarterValue : '—'} awarded this quarter
                        </div>
                      </div>
                    </div>
                  );
                }}
              />
              {goalAllTime != null ? (
                <ReferenceLine y={goalAllTime} stroke="#fa5252" strokeDasharray="4 4" ifOverflow="extendDomain" />
              ) : null}
              <Line
                type="monotone"
                dataKey="cumulative"
                name="Cumulative Credentials"
                stroke={theme.colors.blue[6]}
                strokeWidth={2}
                dot={(dotProps: any) => {
                  const pointKey = dotProps?.payload?.quarter ?? dotProps?.index ?? `${dotProps?.cx}-${dotProps?.cy}`;
                  return (
                    <circle
                      key={`tcc-credentials-dot-${pointKey}`}
                      cx={dotProps.cx}
                      cy={dotProps.cy}
                      r={2}
                      fill={theme.colors.blue[6]}
                      stroke={theme.colorScheme === 'dark' ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.8)'}
                      strokeWidth={1.5}
                    />
                  );
                }}
                activeDot={{ r: 4 }}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Stack>
    </CardShell>
  );
}
